import { useEffect, useState, useMemo, useRef } from "react";
import { useFeedbackFlowStore } from "../stores/feedback-flow-store";
import { useFormConfigStore } from "../stores/form-config-store";
import { useUnsavedChanges } from "../hooks/use-unsaved-changes";
import DynamicPageRenderer from "./dynamic-page-renderer";
import StepExpired from "./steps/step-expired";

const SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes

export default function FeedbackFlow() {
	const {
		currentStepIndex,
		flowSteps,
		prevStep,
		canGoBack,
		initializeFlow,
		reset,
		getCurrentStep,
		isExpired,
		setExpired,
		isComplete,
	} = useFeedbackFlowStore();

	const { brand, brandkit, formSlug, getQuestions, getIsDarkMode } =
		useFormConfigStore();

	const [animationKey, setAnimationKey] = useState(0);
	const sessionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Activate unsaved changes warning
	useUnsavedChanges();

	// Initialize flow when form config is available
	useEffect(() => {
		if (formSlug) {
			initializeFlow(formSlug);
		}
		return () => reset();
	}, [formSlug, initializeFlow, reset]);

	// Session timeout timer - starts when form loads
	useEffect(() => {
		if (formSlug && !isComplete && !isExpired) {
			// Clear any existing timer
			if (sessionTimerRef.current) {
				clearTimeout(sessionTimerRef.current);
			}

			// Set new timer for 60 minutes
			sessionTimerRef.current = setTimeout(() => {
				setExpired(true);
			}, SESSION_TIMEOUT_MS);

			// Cleanup on unmount
			return () => {
				if (sessionTimerRef.current) {
					clearTimeout(sessionTimerRef.current);
				}
			};
		}
	}, [formSlug, isComplete, isExpired, setExpired]);

	// Handle step transitions
	useEffect(() => {
		setAnimationKey((prev) => prev + 1);
	}, [currentStepIndex]);

	const currentStep = getCurrentStep();
	const questions = getQuestions();

	// Calculate question context for question steps
	const questionContext = useMemo(() => {
		if (!currentStep || !currentStep.isQuestionStep) {
			return {
				index: 0,
				total: questions.length,
				currentQuestion: undefined,
			};
		}

		// Find which question we're on based on the step's questionId
		const questionIndex = questions.findIndex(
			(q) => q.id === currentStep.questionId,
		);
		const currentQuestion = questions[questionIndex];

		return {
			index: Math.max(0, questionIndex),
			total: questions.length,
			currentQuestion,
		};
	}, [currentStep, questions]);

	if (!brand || !brandkit) {
		return null;
	}

	// Show expired page if session has timed out
	if (isExpired) {
		return (
			<StepExpired
				brand={brand}
				brandkit={brandkit}
				isDarkMode={getIsDarkMode()}
			/>
		);
	}

	if (!currentStep) {
		return null;
	}

	const showBackButton = canGoBack();
	const stepKey = `${currentStep.page.id}-${currentStep.questionId || ""}-${animationKey}`;

	return (
		<DynamicPageRenderer
			brandkit={brandkit}
			brand={brand}
			page={currentStep.page}
			question={questionContext.currentQuestion}
			questions={questions}
			showBackButton={showBackButton}
			onBack={prevStep}
			stepKey={stepKey}
			currentQuestionIndex={questionContext.index}
			totalQuestions={questionContext.total}
			totalSteps={flowSteps.length}
			currentStepIndex={currentStepIndex}
			isDarkMode={getIsDarkMode()}
			isQuestionStep={currentStep.isQuestionStep}
		/>
	);
}
